import React, { Component } from 'react';
import { injectIntl } from 'react-intl';
import t from 'prop-types';
import Tooltip from '@Components/Tooltip';
import CurrencyInput from '@Components/CurrencyInput';
import OccupationField from '@Components/OccupationField';
import LocationField from '@Components/LocationField';
import Button from '@UI/Button';
import maskSalary from 'maskSalary';
import { AppContext } from 'appContext';
import { withRouter } from 'react-router-dom';
import validateSearchForm from '@Utils/validateSearchForm';

class SearchForm extends Component {
  /**
   * Remove the overlay which appears whenever form input field got a focus.
   */
  static clearFormOverlay = () => {
    const overlay = document.querySelector('.modal__overlay--active');
    const popup = document.querySelector('.popover--active');
    const trigger = document.querySelector('.trigger--active');
    const searchWrapper = document.querySelector('.input-highlight-wrapper--active');

    if (overlay) overlay.classList.remove('modal__overlay--active');
    if (popup) popup.classList.remove('popover--active');
    if (trigger) trigger.classList.remove('trigger--active');
    if (searchWrapper) {
      searchWrapper.removeAttribute('style');
      searchWrapper.classList.remove('input-highlight-wrapper--active');
    }
  };

  constructor(props, context) {
    super(props, context);
    const { currencySettings: { salarySeparator } } = context;

    this.salarySeparator = salarySeparator;

    this.state = {
      salary: maskSalary(props.searchParams.salary, salarySeparator),
      isHeaderSticky: false,
      errors: { occupation: [], location: [] },
    };
  }

  /**
   * Component is fully rendered.
   */
  componentDidMount() {
    window.addEventListener('scroll', this.checkStickyHeader, true);
  }

  /**
   * Called right after updating occurs. Not called for the initial render.
   * @param prevProps
   */
  componentDidUpdate(prevProps) {
    const { searchParams } = this.props;
    if (searchParams.salary !== prevProps.searchParams.salary) {
      this.setState({ salary: maskSalary(searchParams.salary, this.salarySeparator) });
    }
    this.displayErrorsBackground();
  }

  /**
   * Component will unmount.
   */
  componentWillUnmount() {
    window.removeEventListener('scroll', this.checkStickyHeader);
  }

  /**
   * Handle Salary field updates
   * @param e {Event}
   */
  onSalaryChange = (e) => {
    e.target.value = maskSalary(e.target.value, this.salarySeparator);
    this.setState({ salary: e.target.value });
  };

  /**
   * Perform redirect to a result page after form submission.
   *
   * URL formats:
   *  /result-page/{occupationVal}/{locationVal:region}/{locationVal:city}?salary={salaryVal}
   *  /result-page/{occupationVal}/{locationVal:city}?salary={salaryVal}
   *
   * @param {String} url
   *   URL to results page.
   */
  redirectSubmit = (url) => {
    // Update browser history.
    this.props.history.push(`${this.props.baseUrl}${url}`);
    // handle submit from sticky menu to scroll to the main content;
    this.scrollToContainer();
  };

  /**
   * Form submit handler
   * @param e
   */
  formSubmit = async (e) => {
    e.preventDefault();
    // TODO: Replace with id and pass it components.
    const occupation = document.querySelector('.search-form .occupation-field input').value;
    const location = process.env.REACT_APP_COUNTRY !== 'sg'
      ? document.querySelector('.search-form .location-field input').value
      : '';
    const { url, errors, isValid } = await validateSearchForm({
      // Suppressed temporary.
      // eslint-disable-next-line react/no-access-state-in-setstate
      occupation, location, salary: this.state.salary, language: this.props.intl.locale, salarySeparator: this.salarySeparator, intl: this.props.intl,
    });

    // On form submit always:
    // clear form overlay;
    SearchForm.clearFormOverlay();

    this.setState({ errors });

    if (isValid) {
      this.redirectSubmit(url);
    }
  };

  /**
   * Submit form by Enter keydown
   * @param e {KeyboardEvent}
   */
  enterSubmit = (e) => {
    // Submit search on enter key press.
    if (e.key === 'Enter') {
      this.formSubmit(e);
    }
  };

  /**
   * Render error message.
   *
   * @param {String} name
   *   Key of the error message of the input.
   */
  renderError = (name) => (
    this.state.errors[name]?.length > 0
      ? <div className="form-group__feedback">{this.state.errors[name]}</div>
      : ''
  );

  /**
   * Highlight field with error.
   *
   * @param {String} name
   *   Key of the error message of the input.
   */
  highlightFieldError = (name) => (this.state.errors[name].length > 0 ? 'form-group--error' : '');

  /**
   * Check if the header is Sticky or not and update the state respectively.
   */
  checkStickyHeader = () => {
    // Can't unit tests this functionality as it depends on the scroll event.
    /* istanbul ignore next */
    if (document.querySelector('.sticky')) {
      this.setState({ isHeaderSticky: true });
    } else {
      this.setState({ isHeaderSticky: false });
    }
  };

  /**
   * Scroll to the main container if user submit form from sticky header.
   */
  scrollToContainer = () => {
    const container = document.querySelector('.salary-section');
    // Can't unit tests this functionality as it depends on the scroll event.
    /* istanbul ignore next */
    if (container && this.state.isHeaderSticky) {
      container.scrollIntoView({ behavior: 'smooth' });
    }
  };

  /**
   * Display a gap box after the form whenever an errors appear.
   * Background color of the box matched to header background color.
   */
  displayErrorsBackground = () => {
    const { location, occupation } = this.state.errors;
    const ghostBox = document.querySelector('.errors-ghost-box');

    if (location.length || occupation.length) {
      const highestError = [...document.querySelectorAll('.form-group__feedback')].map((item) => item.clientHeight);
      ghostBox.style.height = `${Math.max.apply(null, highestError)}px`;
    } else {
      ghostBox.removeAttribute('style');
    }
  };

  render() {
    const { intl, searchParams } = this.props;
    const { salary } = this.state;
    const backgroundColor = process.env.REACT_APP_BRAND_GUIDELINES === 'true' ? 'bg-variant-brand-primary' : 'bg-variant-brand-secondary';

    return (
      <div
        className={`header-wrapper ${process.env.REACT_APP_COUNTRY === 'sph' ? 'bg-brand--gradient-orange' : backgroundColor}`}
        data-rs-bluex-sticky
        data-rs-bluex-sticky-animate
        data-testid="search-form"
      >
        <div className="wrapper">
          <div
            className="header__content header__content--full-width content-block"
            data-rs-input-highlight-wrapper
          >
            <div className="search-form form-groups form-groups--row items-end">
              <OccupationField
                cssClasses="search-form__input occupation-field"
                id="occupation"
                highlightError={this.highlightFieldError('occupation')}
                renderError={this.renderError('occupation')}
                searchParams={{ occupation: searchParams.occupation }}
              />
              {process.env.REACT_APP_COUNTRY !== 'sg'
                && (
                  <LocationField
                    cssClasses="search-form__input location-field"
                    id="where"
                    highlightError={this.highlightFieldError('location')}
                    renderError={this.renderError('location')}
                    searchParams={{ city: searchParams.city, region: searchParams.region }}
                  />
                )}
              <CurrencyInput
                label={intl.formatMessage({ id: 'Form.SalaryLabel' })}
                tooltip={<Tooltip clickHandler={SearchForm.clearFormOverlay} content={intl.formatMessage({ id: 'Tooltip.Annual.Label' })} />}
                initialValue={salary}
                onChange={this.onSalaryChange}
                onKeyUp={this.onSalaryChange}
                onCurrencyKeyDown={this.enterSubmit}
                inputProps={{
                  id: 'salary',
                  name: 'salary',
                }}
              />
              <div className="form-group">
                {process.env.REACT_APP_BRAND_GUIDELINES === 'true' ? (
                  <Button
                    onClick={this.formSubmit}
                  >
                    {intl.formatMessage({ id: 'Form.BtnLabel' })}
                  </Button>
                ) : (
                  <Button
                    type="filled"
                    size="m"
                    color="blue"
                    onClick={this.formSubmit}
                  >
                    {intl.formatMessage({ id: 'Form.BtnLabel' })}
                  </Button>
                )}
              </div>
            </div>
            <div className="input-overlay modal__overlay" style={{ zIndex: '-1' }} />
          </div>
        </div>
        <div className="errors-ghost-box" />
      </div>
    );
  }
}

SearchForm.contextType = AppContext;

SearchForm.propTypes = {
  /** Form action base url. */
  baseUrl: t.string.isRequired,
  searchParams: t.shape({
    occupation: t.string,
    region: t.string,
    city: t.string,
    salary: t.string,
  }),
};

SearchForm.defaultProps = {
  searchParams: {
    occupation: '',
    region: '',
    city: '',
    salary: '',
  },
};

export default injectIntl(withRouter(SearchForm));
